import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useActivityTracker } from '../hooks/useActivityTracker';
import './ContributionCalendar.css';

// Global request tracker to prevent any duplicate requests across all instances
let globalRequestPromise = null;

const ContributionCalendar = ({ onRefresh }) => {
  const [contributionData, setContributionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);
  const { getContributions } = useActivityTracker();
  const loadingRef = useRef(false);

  const fetchContributions = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    try {
      setLoading(true);
      loadingRef.current = true;
      
      const startDate = new Date(2025, 0, 1); // January 1st, 2025
      
      // Find the Sunday before or on January 1st
      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek !== 0) {
        startDate.setDate(startDate.getDate() - startDayOfWeek);
      }
      
      // Calculate days from start of calendar to current date
      const today = new Date();
      const daysDiff = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
      
      // Smart calculation: only request what we need
      let daysToRequest;
      if (today.getFullYear() < 2025) {
        // If we're still in 2024, request minimal data (just a few days)
        daysToRequest = 50;
      } else if (today.getFullYear() === 2025) {
        // Request from start of calendar year to current date + small buffer
        daysToRequest = Math.min(daysDiff + 7, 380); // Max 380 days
      } else {
        // If we're past 2025, request full year
        daysToRequest = 380;
      }
      
      globalRequestPromise = getContributions(daysToRequest, 2025);
      const data = await globalRequestPromise;
      setContributionData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load contribution data');
      globalRequestPromise = null;
    } finally {
      setLoading(false);
      loadingRef.current = false;
      // Keep the promise for a short time in case of immediate duplicate calls
      setTimeout(() => {
        globalRequestPromise = null;
      }, 1000);
    }
  }, [getContributions]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchContributions);
    }
  }, [fetchContributions, onRefresh]);

  // Initial load only once
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchContributions();
    }
  }, [fetchContributions]);

  // Генерируем данные для последних 365 дней
  const generateCalendarData = () => {
    if (!contributionData?.contributions) {
      return [];
    }

    // Start from January 1st of current year (2025)
    const currentYear = 2025;
    const startDate = new Date(currentYear, 0, 1); // January 1st, 2025
    
    // Find the Sunday before or on January 1st
    const startDayOfWeek = startDate.getDay(); // 0 = Sunday
    if (startDayOfWeek !== 0) {
      startDate.setDate(startDate.getDate() - startDayOfWeek);
    }
    
    const data = [];
    const currentDate = new Date(startDate);
    
    // Создаем lookup для быстрого поиска
    const contributionMap = {};
    contributionData.contributions.forEach(contrib => {
      contributionMap[contrib.date] = contrib;
    });
    
    // Generate data until end of 2025
    const endDate = new Date(2025, 11, 31); // December 31st, 2025
    
    while (currentDate <= endDate) {
      const dateString = currentDate.toISOString().split('T')[0];
      const contributionInfo = contributionMap[dateString] || { count: 0, types: {} };
      
      data.push({
        date: new Date(currentDate),
        count: contributionInfo.count,
        types: contributionInfo.types || {},
        level: getContributionLevel(contributionInfo.count)
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Pad to complete weeks (fill remaining days in the last week)
    while (data.length % 7 !== 0) {
      const lastDate = new Date(data[data.length - 1].date);
      lastDate.setDate(lastDate.getDate() + 1);
      
      data.push({
        date: lastDate,
        count: 0,
        types: {},
        level: 0
      });
    }
    
    return data;
  };
  
  const getContributionLevel = (count) => {
    if (count === 0) return 0;
    if (count <= 3) return 1;
    if (count <= 6) return 2;
    if (count <= 9) return 3;
    return 4;
  };
  
  const getMonthLabels = (calendarData) => {
    // Month names for calendar header (only 2025 months)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (!calendarData || calendarData.length === 0) {
      return [];
    }
    
    const totalWeeks = Math.ceil(calendarData.length / 7);
    const labels = new Array(totalWeeks).fill('');
    
    let lastShownMonth = -1;
    
    // Go through each week and check if we should show a month label
    for (let weekIndex = 0; weekIndex < totalWeeks; weekIndex++) {
      const weekStartIndex = weekIndex * 7;
      if (weekStartIndex >= calendarData.length) break;
      
      const firstDayOfWeek = calendarData[weekStartIndex];
      if (!firstDayOfWeek || !firstDayOfWeek.date) continue;
      
      const weekDate = firstDayOfWeek.date;
      const currentMonth = weekDate.getMonth();
      const currentYear = weekDate.getFullYear();
      
      // Only show months for 2025
      if (currentYear === 2025) {
        // Show month label if:
        // 1. It's the first week with 2025 data
        // 2. Month has changed from last shown month
        // 3. The first day of week is within first 10 days of the month
        if (lastShownMonth === -1 || 
            (currentMonth !== lastShownMonth && weekDate.getDate() <= 10)) {
          labels[weekIndex] = months[currentMonth];
          lastShownMonth = currentMonth;
        }
      }
    }
    
    return labels;
  };

  const formatTooltip = (day) => {
    const activityText = day.count === 1 ? 'activity' : 'activities';
    let tooltip = `${day.count} ${activityText} on ${day.date.toDateString()}`;
    
    if (day.types && Object.keys(day.types).length > 0) {
      const typesList = Object.entries(day.types)
        .map(([type, count]) => `${count} ${type.replace('_', ' ')}`)
        .join(', ');
      tooltip += `\n${typesList}`;
    }
    
    return tooltip;
  };

  if (loading) {
    return (
      <div className="contribution-calendar">
        <div className="loading-skeleton">
          <div className="skeleton-grid"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contribution-calendar">
        <div className="calendar-header">
          <h3>Error loading contributions</h3>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const calendarData = generateCalendarData();
  const monthLabels = getMonthLabels(calendarData);
  
  // Группируем дни по неделям
  const weeks = [];
  for (let i = 0; i < calendarData.length; i += 7) {
    weeks.push(calendarData.slice(i, i + 7));
  }
  
  return (
    <div className="contribution-calendar">
      <div className="calendar-container">
        <div className="month-labels">
          {monthLabels.map((month, index) => (
            <span key={index} className="month-label">{month}</span>
          ))}
        </div>
        
        <div className="calendar-content">
          <div className="day-labels">
            <span></span>
            <span className="day-label">Mon</span>
            <span></span>
            <span className="day-label">Wed</span>
            <span></span>
            <span className="day-label">Fri</span>
            <span></span>
          </div>
          
          <div className="calendar-grid">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="calendar-week">
                {week.map((day, dayIndex) => (
                  <div
                    key={`${weekIndex}-${dayIndex}`}
                    className={`calendar-day level-${day.level}`}
                    title={formatTooltip(day)}
                    data-count={day.count}
                    data-date={day.date.toISOString().split('T')[0]}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContributionCalendar; 